import { createSlice } from '@reduxjs/toolkit';
import monitoringService from '../services/monitoringService';
import { WorkQueue } from './models/work-queue.model';
import { Task } from './models/task.model';
import * as uiSlice from './ui.slice';
import {
  CreatableWorkflow,
  DraftCreatableWorkflow,
  EditableWorkflow,
  Workflow,
  WorkflowState,
} from './models/workflow.model';
import { SystemEvent } from './models/system-event.model';
import { ActivityLogItem } from './models/activity-log-item.model';
import userService from '../services/userService';
import uniq from 'lodash/uniq';
import { forkJoin } from 'rxjs';
import {
  CommsCenterEmailLog,
  CommsCenterLogPagedList,
  CommsCenterSMSLog,
} from './models/comms-center-log.model';
import {
  GetWorkflowHistoryResponse,
  WorkflowExecutionDetails,
  WorkflowExecutionState,
  WorkflowExecutionStatus,
} from './models/workflow-executions.model';

interface MonitoringState {
  workQueues: WorkQueue[];
  loadingWorkQueues: boolean;
  loadedWorkQueues: boolean;
  savingWorkQueue: boolean;
  savedWorkQueue: boolean;
  currentWorkQueueList: Task[];
  loadingWorkQueueList: boolean;
  loadedWorkQueueList: boolean;
  userTasks: Task[];
  loadingUserTasks: boolean;
  loadedUserTasks: boolean;
  savingTask: boolean;
  savedTask: boolean;
  savingWorkflow: boolean;
  savedWorkflow: boolean;
  createdWorkflow: boolean;
  loadingWorkflows: boolean;
  loadedWorkflows: boolean;
  deletedWorkflow: boolean;
  workflows: Workflow[] | null;
  draftWorkflow: DraftCreatableWorkflow | null;
  systemEvents: SystemEvent[];
  activityLog: ActivityLogItem[];
  loadingActivityLog: boolean;
  loadedActivityLog: boolean;
  smsNotificationLogs: CommsCenterLogPagedList<CommsCenterSMSLog>;
  emailNotificationLogs: CommsCenterLogPagedList<CommsCenterEmailLog>;
  workflowHistory: GetWorkflowHistoryResponse | null;
  abortingWorkflowExecution: boolean;
  abortedWorkflowExecution: boolean;
  workflowExecution: WorkflowExecutionDetails | null;
}

export const initialState: MonitoringState = {
  workQueues: [],
  loadingWorkQueues: false,
  loadedWorkQueues: false,
  savingWorkQueue: false,
  savedWorkQueue: false,
  currentWorkQueueList: [],
  loadingWorkQueueList: false,
  loadedWorkQueueList: false,
  userTasks: [],
  loadingUserTasks: false,
  loadedUserTasks: false,
  savingTask: false,
  savedTask: false,
  savingWorkflow: false,
  savedWorkflow: false,
  createdWorkflow: false,
  loadingWorkflows: false,
  loadedWorkflows: false,
  deletedWorkflow: false,
  workflows: null,
  draftWorkflow: null,
  systemEvents: [],
  activityLog: [],
  loadingActivityLog: false,
  loadedActivityLog: false,
  smsNotificationLogs: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    messages: [],
  },
  emailNotificationLogs: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    messages: [],
  },
  workflowHistory: null,
  abortingWorkflowExecution: false,
  abortedWorkflowExecution: false,
  workflowExecution: null,
};

export const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setWorkQueues: (
      state: MonitoringState,
      action: { type: string; payload: WorkQueue[] },
    ) => {
      state.workQueues = action.payload;
    },
    setLoadingWorkQueues: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingWorkQueues = action.payload;
    },
    setLoadedWorkQueues: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedWorkQueues = action.payload;
    },
    setSavingWorkQueue: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingWorkQueue = action.payload;
    },
    setSavedWorkQueue: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedWorkQueue = action.payload;
    },
    addWorkQueue: (
      state: MonitoringState,
      action: { type: string; payload: WorkQueue },
    ) => {
      state.workQueues.push(action.payload);
    },
    updateWorkQueue: (
      state: MonitoringState,
      action: { type: string; payload: WorkQueue },
    ) => {
      const workQueues = [...state.workQueues];
      const idx = workQueues.findIndex(
        (tq) => tq.workQueueUUID === action.payload.workQueueUUID,
      );
      if (idx >= 0) {
        workQueues.splice(idx, 1, action.payload);
      }
      state.workQueues = workQueues;
    },
    deleteWorkQueue: (
      state: MonitoringState,
      action: { type: string; payload: string },
    ) => {
      const workQueues = [...state.workQueues];
      const idx = workQueues.findIndex(
        (tq) => tq.workQueueUUID === action.payload,
      );
      if (idx >= 0) {
        workQueues.splice(idx, 1);
      }
      state.workQueues = workQueues;
    },
    setCurrentWorkQueueList: (
      state: MonitoringState,
      action: { type: string; payload: Task[] },
    ) => {
      state.currentWorkQueueList = action.payload;
    },
    setLoadingWorkQueueList: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingWorkQueueList = action.payload;
    },
    setLoadedWorkQueueList: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedWorkQueueList = action.payload;
    },
    setUserTasks: (
      state: MonitoringState,
      action: { type: string; payload: Task[] },
    ) => {
      state.userTasks = action.payload;
    },
    setLoadingUserTasks: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUserTasks = action.payload;
    },
    setLoadedUserTasks: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUserTasks = action.payload;
    },
    setSavingTask: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTask = action.payload;
    },
    setSavedTask: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTask = action.payload;
    },
    removeTask: (
      state: MonitoringState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      const tasks = [...state.currentWorkQueueList];
      const idx = tasks.findIndex((t) => t.taskUUID === action.payload);
      if (idx >= 0) {
        tasks.splice(idx, 1);
        state.currentWorkQueueList = tasks;
      }
    },
    setSavingWorkflow: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingWorkflow = action.payload;
    },
    setSavedWorkflow: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedWorkflow = action.payload;
    },
    setCreatedWorkflow: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.createdWorkflow = action.payload;
    },
    setLoadingWorkflows: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingWorkflows = action.payload;
    },
    setLoadedWorkflows: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedWorkflows = action.payload;
    },
    setWorkflows: (
      state: MonitoringState,
      action: { type: string; payload: Workflow[] },
    ) => {
      state.workflows = action.payload;
    },
    setDraftWorkflow: (
      state: MonitoringState,
      action: { type: string; payload: DraftCreatableWorkflow | null },
    ) => {
      state.draftWorkflow = action.payload;
    },
    setSystemEvents: (
      state: MonitoringState,
      action: { type: string; payload: SystemEvent[] },
    ) => {
      state.systemEvents = action.payload;
    },
    setDeletedWorkflow: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.deletedWorkflow = action.payload;
    },
    updateWorkflowState: (
      state: MonitoringState,
      action: {
        type: string;
        payload: { workflowUUID: string; state: WorkflowState };
      },
    ) => {
      const matchingWorkflow = state.workflows?.find(
        (workflow) => workflow.workflowUUID === action.payload.workflowUUID,
      );
      if (matchingWorkflow) {
        matchingWorkflow.state = action.payload.state;
        state.workflows = [...state.workflows!];
      }
    },
    setActivityLog: (
      state: MonitoringState,
      action: { type: string; payload: ActivityLogItem[] },
    ) => {
      state.activityLog = action.payload;
    },
    setLoadingActivityLog: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingActivityLog = action.payload;
    },
    setLoadedActivityLog: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedActivityLog = action.payload;
    },
    setSMSNotificationLogs: (
      state: MonitoringState,
      action: {
        type: string;
        payload: CommsCenterLogPagedList<CommsCenterSMSLog>;
      },
    ) => {
      state.smsNotificationLogs = action.payload;
    },
    setEmailNotificationLogs: (
      state: MonitoringState,
      action: {
        type: string;
        payload: CommsCenterLogPagedList<CommsCenterEmailLog>;
      },
    ) => {
      state.emailNotificationLogs = action.payload;
    },
    setWorkflowHistory: (
      state: MonitoringState,
      action: {
        type: string;
        payload: {
          response: GetWorkflowHistoryResponse;
          requestContinuationToken?: string;
        };
      },
    ) => {
      const { response, requestContinuationToken } = action.payload;
      const { workflowExecutions, continuationToken } = action.payload.response;
      if (
        requestContinuationToken &&
        state.workflowHistory &&
        requestContinuationToken === state.workflowHistory.continuationToken
      ) {
        state.workflowHistory = {
          workflowExecutions: [
            ...state.workflowHistory.workflowExecutions,
            ...workflowExecutions,
          ],
          continuationToken,
        };
      } else {
        state.workflowHistory = response;
      }
    },
    setAbortingWorkflowExecution: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.abortingWorkflowExecution = action.payload;
    },
    setAbortedWorkflowExecution: (
      state: MonitoringState,
      action: { type: string; payload: boolean },
    ) => {
      state.abortedWorkflowExecution = action.payload;
    },
    setWorkflowExecution: (
      state: MonitoringState,
      action: { type: string; payload: WorkflowExecutionDetails },
    ) => {
      state.workflowExecution = action.payload;
    },
  },
});

export const { setWorkQueues } = slice.actions;
export const { setLoadingWorkQueues } = slice.actions;
export const { setLoadedWorkQueues } = slice.actions;
export const { setSavingWorkQueue } = slice.actions;
export const { setSavedWorkQueue } = slice.actions;
export const { addWorkQueue } = slice.actions;
export const { updateWorkQueue } = slice.actions;
export const { deleteWorkQueue } = slice.actions;
export const { setCurrentWorkQueueList } = slice.actions;
export const { setLoadingWorkQueueList } = slice.actions;
export const { setLoadedWorkQueueList } = slice.actions;
export const { setUserTasks } = slice.actions;
export const { setLoadingUserTasks } = slice.actions;
export const { setLoadedUserTasks } = slice.actions;
export const { setSavingTask } = slice.actions;
export const { setSavedTask } = slice.actions;
export const { removeTask } = slice.actions;
export const { setSavingWorkflow } = slice.actions;
export const { setSavedWorkflow } = slice.actions;
export const { setCreatedWorkflow } = slice.actions;
export const { setLoadedWorkflows } = slice.actions;
export const { setLoadingWorkflows } = slice.actions;
export const { setWorkflows } = slice.actions;
export const { setDraftWorkflow } = slice.actions;
export const { setSystemEvents } = slice.actions;
export const { setDeletedWorkflow } = slice.actions;
export const { updateWorkflowState } = slice.actions;
export const { setActivityLog } = slice.actions;
export const { setLoadingActivityLog } = slice.actions;
export const { setLoadedActivityLog } = slice.actions;
export const { setSMSNotificationLogs } = slice.actions;
export const { setEmailNotificationLogs } = slice.actions;
export const { setWorkflowHistory } = slice.actions;
export const { setAbortingWorkflowExecution } = slice.actions;
export const { setAbortedWorkflowExecution } = slice.actions;
export const { setWorkflowExecution } = slice.actions;

export const fetchWorkQueuesAsync = () => (dispatch: Function) => {
  dispatch(setWorkQueues([]));
  dispatch(setLoadedWorkQueues(true));
  dispatch(setLoadingWorkQueues(true));
  monitoringService.getWorkQueues().subscribe({
    next: (workQueues) => {
      dispatch(setWorkQueues(workQueues));
      dispatch(setLoadingWorkQueues(false));
      dispatch(setLoadedWorkQueues(true));
    },
    error: (err) => {
      dispatch(setLoadingWorkQueues(false));
      dispatch(setLoadedWorkQueues(false));
    },
  });
};

export const createWorkQueueAsync =
  (workQueue: WorkQueue) => (dispatch: Function) => {
    dispatch(setSavingWorkQueue(true));
    dispatch(setSavedWorkQueue(false));
    monitoringService.createWorkQueue(workQueue).subscribe({
      next: (newQueue) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createWorkQueueModelSuccess'),
        );
        dispatch(addWorkQueue(newQueue));
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(true));
      },
      error: (err) => {
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(false));
      },
    });
  };

export const updateWorkQueueAsync =
  (workQueue: WorkQueue) => (dispatch: Function) => {
    dispatch(setSavingWorkQueue(true));
    dispatch(setSavedWorkQueue(false));
    monitoringService.updateWorkQueue(workQueue).subscribe({
      next: (newQueue) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateWorkQueueModelSuccess'),
        );
        dispatch(updateWorkQueue(newQueue));
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(true));
      },
      error: (err) => {
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(false));
      },
    });
  };

export const activateWorkQueueAsync =
  (workQueue: WorkQueue) => (dispatch: Function) => {
    dispatch(setSavingWorkQueue(true));
    dispatch(setSavedWorkQueue(false));
    monitoringService.activateWorkQueue(workQueue).subscribe({
      next: (newQueue) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.activateWorkQueueModelSuccess'),
        );
        dispatch(updateWorkQueue(newQueue));
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(true));
      },
      error: (err) => {
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(false));
      },
    });
  };

export const deactivateWorkQueueAsync =
  (workQueue: WorkQueue) => (dispatch: Function) => {
    dispatch(setSavingWorkQueue(true));
    dispatch(setSavedWorkQueue(false));
    monitoringService.deactivateWorkQueue(workQueue).subscribe({
      next: (newQueue) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deactivateWorkQueueModelSuccess'),
        );
        dispatch(updateWorkQueue(newQueue));
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(true));
      },
      error: (err) => {
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(false));
      },
    });
  };

export const deleteWorkQueueAsync =
  (workQueue: WorkQueue) => (dispatch: Function) => {
    dispatch(setSavingWorkQueue(true));
    dispatch(setSavedWorkQueue(false));
    monitoringService.deleteWorkQueue(workQueue).subscribe({
      next: () => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deleteWorkQueueModelSuccess'),
        );
        dispatch(deleteWorkQueue(workQueue.workQueueUUID!));
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(true));
      },
      error: (err) => {
        dispatch(setSavingWorkQueue(false));
        dispatch(setSavedWorkQueue(false));
      },
    });
  };

export const fetchWorkQueueListAsync =
  (workQueueUUID: string) => (dispatch: Function) => {
    dispatch(setCurrentWorkQueueList([]));
    dispatch(setLoadedWorkQueueList(true));
    monitoringService.getTasksForQueue(workQueueUUID).subscribe({
      next: (WorkQueue) => {
        dispatch(setCurrentWorkQueueList(WorkQueue));
        dispatch(setLoadingWorkQueueList(false));
        dispatch(setLoadedWorkQueueList(true));
      },
      error: (err) => {
        dispatch(setLoadingWorkQueueList(false));
        dispatch(setLoadedWorkQueueList(false));
      },
    });
  };

export const fetchUserTasksAsync = (userID: string) => (dispatch: Function) => {
  dispatch(setUserTasks([]));
  dispatch(setLoadedUserTasks(true));
  monitoringService.getTasksForUser(userID).subscribe({
    next: (tasks) => {
      dispatch(setUserTasks(tasks));
      dispatch(setLoadingUserTasks(false));
      dispatch(setLoadedUserTasks(true));
    },
    error: (err) => {
      dispatch(setLoadingUserTasks(false));
      dispatch(setLoadedUserTasks(false));
    },
  });
};

export const escalateTaskAsync =
  (taskUUID: string, reasonBCs: string[]) => (dispatch: Function) => {
    dispatch(setSavingTask(true));
    monitoringService.escalateTask(taskUUID, reasonBCs).subscribe({
      next: (task) => {
        dispatch(setSavingTask(false));
        dispatch(setSavedTask(true));
        dispatch(uiSlice.setSuccessMessage('snackbar.taskEscalatedSuccess'));
      },
      error: (err) => {
        dispatch(setSavingTask(false));
        dispatch(setSavedTask(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const resolveTaskAsync =
  (taskUUID: string, reasonBCs: string[]) => (dispatch: Function) => {
    dispatch(setSavingTask(true));
    monitoringService.resolveTask(taskUUID, reasonBCs).subscribe({
      next: (task) => {
        dispatch(setSavingTask(false));
        dispatch(setSavedTask(true));
        dispatch(removeTask(taskUUID));
        dispatch(uiSlice.setSuccessMessage('snackbar.taskResolvedSuccess'));
      },
      error: (err) => {
        dispatch(setSavingTask(false));
        dispatch(setSavedTask(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const createWorkflowAsync =
  (workflow: CreatableWorkflow) => (dispatch: Function) => {
    dispatch(setSavingWorkflow(true));
    dispatch(setCreatedWorkflow(false));
    monitoringService.createWorkflow(workflow).subscribe({
      next: (newWorkflow: Workflow) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setCreatedWorkflow(true));
        dispatch(uiSlice.setSuccessMessage('snackbar.createWorkflowSuccess'));
      },
      error: (err) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setCreatedWorkflow(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchWorkflowAsync =
  (workflowUUID: string, version?: number) => (dispatch: Function) => {
    dispatch(setLoadingWorkflows(true));
    dispatch(setLoadedWorkflows(false));
    monitoringService.getWorkflow(workflowUUID, version).subscribe({
      next: (workflow: Workflow) => {
        dispatch(setLoadingWorkflows(false));
        dispatch(setLoadedWorkflows(true));
        dispatch(setWorkflows([workflow]));
      },
      error: (err) => {
        dispatch(setLoadingWorkflows(false));
        dispatch(setLoadedWorkflows(false));
      },
    });
  };

export const fetchWorkflowsAsync =
  (workQueueUUID: string, states?: WorkflowState[]) => (dispatch: Function) => {
    dispatch(setLoadingWorkflows(true));
    dispatch(setLoadedWorkflows(false));
    monitoringService.getWorkflowsByState(workQueueUUID, states).subscribe({
      next: (workflows: Workflow[]) => {
        dispatch(setLoadingWorkflows(false));
        dispatch(setLoadedWorkflows(true));
        dispatch(setWorkflows(workflows));
      },
      error: (err) => {
        dispatch(setLoadingWorkflows(false));
        dispatch(setLoadedWorkflows(false));
      },
    });
  };

export const activateWorkflowAsync =
  (workflowUUID: string) => (dispatch: Function) => {
    dispatch(setSavingWorkflow(true));
    dispatch(setSavedWorkflow(false));
    monitoringService.activateWorkflow(workflowUUID).subscribe({
      next: (workflow: Workflow) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setSavedWorkflow(true));
        dispatch(uiSlice.setSuccessMessage('snackbar.activateWorkflowSuccess'));
      },
      error: (err) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setSavedWorkflow(false));
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(fetchWorkflowAsync(workflowUUID));
      },
    });
  };

export const deactivateWorkflowAsync =
  (workflowUUID: string) => (dispatch: Function) => {
    dispatch(setSavingWorkflow(true));
    dispatch(setSavedWorkflow(false));
    monitoringService.deactivateWorkflow(workflowUUID).subscribe({
      next: (workflow: Workflow) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setSavedWorkflow(true));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deactivateWorkflowSuccess'),
        );
      },
      error: (err) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setSavedWorkflow(false));
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(fetchWorkflowAsync(workflowUUID));
      },
    });
  };

export const updateWorkflowAsync =
  (workflow: EditableWorkflow) => (dispatch: Function) => {
    dispatch(setSavingWorkflow(true));
    dispatch(setSavedWorkflow(false));
    monitoringService.updateWorkflow(workflow).subscribe({
      next: (workflow: Workflow) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updateWorkflowSuccess'));
        dispatch(setSavingWorkflow(false));
        dispatch(setSavedWorkflow(true));
      },
      error: (err) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setSavedWorkflow(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const deleteWorkflowAsync =
  (workflowUUID: string) => (dispatch: Function) => {
    dispatch(setSavingWorkflow(true));
    dispatch(setDeletedWorkflow(false));
    monitoringService.deleteWorkflow(workflowUUID).subscribe({
      next: (workflowUUID: string) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.deleteWorkflowSuccess'));
        dispatch(setSavingWorkflow(false));
        dispatch(setDeletedWorkflow(true));
      },
      error: (err) => {
        dispatch(setSavingWorkflow(false));
        dispatch(setDeletedWorkflow(false));
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(fetchWorkflowAsync(workflowUUID));
      },
    });
  };

export const validateWorkflowAsync =
  (workflow: CreatableWorkflow) => (dispatch: Function) => {
    monitoringService.validateWorkflow(workflow).subscribe({
      next: (response) => {
        if (response?.issues?.length > 0) {
          dispatch(uiSlice.setErrorMessage(response.message));
        }
      },
    });
  };

export const fetchSystemEventsAsync = () => (dispatch: Function) => {
  monitoringService.getSystemEvents().subscribe({
    next: (systemEvents) => {
      dispatch(setSystemEvents(systemEvents));
    },
    error: (err) => {
      dispatch(uiSlice.setErrorMessage(err.message));
    },
  });
};

export const fetchActivityLogAsync =
  (correlationUUID: string) => (dispatch: Function) => {
    dispatch(setActivityLog([]));
    dispatch(setLoadingActivityLog(true));
    dispatch(setLoadedActivityLog(false));
    monitoringService.getActivityLog(correlationUUID).subscribe({
      next: (activityLog) => {
        const userRequests = uniq(
          activityLog
            .filter((al) => al.modifiedByUserID !== '0')
            .map((al) => al.modifiedByUserID),
        ).map((id) => userService.getUserInfo(Number(id)));
        forkJoin(userRequests).subscribe((users) => {
          activityLog.forEach((al) => {
            const user = users.find(
              (u) => String(u.userAccountID) === al.modifiedByUserID,
            );
            al.userName = user
              ? `${user.userFirstName} ${user.userLastName}`
              : '';
          });
          dispatch(setActivityLog(activityLog));
          dispatch(setLoadingActivityLog(false));
          dispatch(setLoadedActivityLog(true));
        });
      },
      error: (err) => {
        dispatch(setLoadingActivityLog(false));
      },
    });
  };

export const fetchSMSNotificationLogsAsync =
  (
    workflowUUID: string,
    startDate: string,
    endDate: string,
    page: number,
    pageSize: number,
    phoneNumber?: string,
  ) =>
  (dispatch: Function) => {
    monitoringService
      .getSMSNotificationLogs(
        workflowUUID,
        startDate,
        endDate,
        page,
        pageSize,
        phoneNumber,
      )
      .subscribe({
        next: (logs) => {
          dispatch(setSMSNotificationLogs(logs));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const fetchEmailNotificationLogsAsync =
  (
    workflowUUID: string,
    startDate: string,
    endDate: string,
    page: number,
    pageSize: number,
    email?: string,
  ) =>
  (dispatch: Function) => {
    monitoringService
      .getEmailNotificationLogs(
        workflowUUID,
        startDate,
        endDate,
        page,
        pageSize,
        email,
      )
      .subscribe({
        next: (logs) => {
          dispatch(setEmailNotificationLogs(logs));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const fetchWorkflowHistoryAsync =
  (
    workflowUUID: string,
    beginDate: string,
    endDate: string,
    state?: WorkflowExecutionState,
    status?: WorkflowExecutionStatus,
    continuationToken?: string,
  ) =>
  (dispatch: Function) => {
    monitoringService
      .getWorkflowHistory(
        workflowUUID,
        beginDate,
        endDate,
        state,
        status,
        continuationToken,
      )
      .subscribe({
        next: (history) => {
          dispatch(
            setWorkflowHistory({
              response: history,
              requestContinuationToken: continuationToken,
            }),
          );
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const abortWorkflowExecution =
  (workflowExecutionUUID: string) => (dispatch: Function) => {
    dispatch(setAbortedWorkflowExecution(false));
    dispatch(setAbortingWorkflowExecution(true));
    monitoringService.abortWorkflowExecution(workflowExecutionUUID).subscribe({
      next: () => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.abortWorkflowExecutionSuccess'),
        );
        dispatch(setAbortedWorkflowExecution(true));
        dispatch(setAbortingWorkflowExecution(false));
      },
      error: (err) => {
        dispatch(setAbortingWorkflowExecution(false));
        dispatch(setAbortedWorkflowExecution(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchWorkflowExecutionAsync =
  (workflowExecutionUUID: string) => (dispatch: Function) => {
    monitoringService.getWorkflowExecution(workflowExecutionUUID).subscribe({
      next: (workflowExecution: WorkflowExecutionDetails) => {
        dispatch(setWorkflowExecution(workflowExecution));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const selectWorkQueues = (state: { monitoring: MonitoringState }) =>
  state.monitoring.workQueues;
export const selectLoadingWorkQueues = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.loadingWorkQueues;
export const selectLoadedWorkQueues = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.loadedWorkQueues;
export const selectCurrentWorkQueueList = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.currentWorkQueueList;
export const selectLoadingWorkQueueList = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.loadingWorkQueueList;
export const selectLoadedWorkQueueList = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.loadedWorkQueueList;
export const selectUserTasks = (state: { monitoring: MonitoringState }) =>
  state.monitoring.userTasks;
export const selectLoadingUserTasks = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.loadingUserTasks;
export const selectLoadedUserTasks = (state: { monitoring: MonitoringState }) =>
  state.monitoring.loadedUserTasks;
export const selectLoadingTask = (state: { monitoring: MonitoringState }) =>
  state.monitoring.savingTask;
export const selectLoadedTask = (state: { monitoring: MonitoringState }) =>
  state.monitoring.savedTask;
export const selectSavingWorkflow = (state: { monitoring: MonitoringState }) =>
  state.monitoring.savingWorkflow;
export const selectSavedWorkflow = (state: { monitoring: MonitoringState }) =>
  state.monitoring.savedWorkflow;
export const selectCreatedWorkflow = (state: { monitoring: MonitoringState }) =>
  state.monitoring.createdWorkflow;
export const selectLoadingWorkflows = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.loadingWorkflows;
export const selectLoadedWorkflows = (state: { monitoring: MonitoringState }) =>
  state.monitoring.loadedWorkflows;
export const selectWorkflows = (state: { monitoring: MonitoringState }) =>
  state.monitoring.workflows;
export const selectDraftWorkflow = (state: { monitoring: MonitoringState }) =>
  state.monitoring.draftWorkflow;
export const selectSystemEvents = (state: { monitoring: MonitoringState }) =>
  state.monitoring.systemEvents;
export const selectDeletedWorkflow = (state: { monitoring: MonitoringState }) =>
  state.monitoring.deletedWorkflow;
export const selectWorkflow = (
  state: { monitoring: MonitoringState },
  workflowUUID: string,
) => {
  return state.monitoring.workflows?.find(
    (workflow) => workflow.workflowUUID === workflowUUID,
  );
};
export const selectActivityLog = (state: { monitoring: MonitoringState }) =>
  state.monitoring.activityLog;
export const selectSMSNotificationLogs = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.smsNotificationLogs;
export const selectEmailNotificationLogs = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.emailNotificationLogs;
export const selectWorkflowHistory = (state: { monitoring: MonitoringState }) =>
  state.monitoring.workflowHistory;
export const selectAbortedWorkflowExecution = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.abortedWorkflowExecution;
export const selectAbortingWorkflowExecution = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.abortingWorkflowExecution;
export const selectWorkflowExecution = (state: {
  monitoring: MonitoringState;
}) => state.monitoring.workflowExecution;

export default slice.reducer;
